import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { remindersAPI, notificationsAPI, pushAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Bell,
  BellRing,
  AlertTriangle,
  Clock,
  Mail,
  Settings,
  Gauge,
  Calendar,
  ChevronRight,
  Truck,
  Check,
  Smartphone,
} from "lucide-react";

export default function Reminders() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingPush, setSendingPush] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [subscribingPush, setSubscribingPush] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [reminderSummary, setReminderSummary] = useState({ overdue: 0, dueSoon: 0 });
  const [preferences, setPreferences] = useState({
    email_enabled: false,
    dashboard_alerts: true,
    default_reminder_miles: 500,
    default_reminder_days: 30,
    email_configured: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [remindersRes, prefsRes, pushStatusRes] = await Promise.all([
        remindersAPI.list(),
        notificationsAPI.getPreferences(),
        pushAPI.status().catch(() => ({ data: { subscribed: false, push_enabled: false } })),
      ]);
      setReminders(remindersRes.data.reminders);
      setReminderSummary({
        overdue: remindersRes.data.overdue_count,
        dueSoon: remindersRes.data.due_soon_count,
      });
      setPreferences(prefsRes.data);
      setPushSubscribed(pushStatusRes.data.subscribed);
      setPushEnabled(pushStatusRes.data.push_enabled);
    } catch (error) {
      toast.error("Failed to load reminders");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const { data } = await notificationsAPI.updatePreferences({
        email_enabled: preferences.email_enabled,
        dashboard_alerts: preferences.dashboard_alerts,
        default_reminder_miles: preferences.default_reminder_miles,
        default_reminder_days: preferences.default_reminder_days,
      });
      setPreferences(data);
      toast.success("Preferences saved");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await remindersAPI.sendEmail();
      if (data.sent) {
        toast.success("Reminder email sent!");
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Helper function to convert base64 to Uint8Array for VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const handleSubscribePush = async () => {
    setSubscribingPush(true);
    try {
      // Check if browser supports push
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        toast.error("Push notifications are not supported in this browser");
        return;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Notification permission denied");
        return;
      }

      // Get VAPID public key
      const { data: vapidData } = await pushAPI.getVapidKey();
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey)
      });

      // Send subscription to backend
      await pushAPI.subscribe({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
        }
      });

      setPushSubscribed(true);
      toast.success("Push notifications enabled!");
    } catch (error) {
      console.error("Push subscription error:", error);
      toast.error(error.response?.data?.detail || "Failed to enable push notifications");
    } finally {
      setSubscribingPush(false);
    }
  };

  const handleUnsubscribePush = async () => {
    setSubscribingPush(true);
    try {
      await pushAPI.unsubscribe();
      
      // Also unsubscribe from browser
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      setPushSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (error) {
      toast.error("Failed to disable push notifications");
    } finally {
      setSubscribingPush(false);
    }
  };

  const handleSendPushTest = async () => {
    setSendingPush(true);
    try {
      const { data } = await pushAPI.sendTest();
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    } finally {
      setSendingPush(false);
    }
  };

  const handleSendPushReminders = async () => {
    setSendingPush(true);
    try {
      const { data } = await pushAPI.sendReminders();
      if (data.sent) {
        toast.success("Push notification sent!");
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send push notification");
    } finally {
      setSendingPush(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="back-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />

                <h1 className="text-lg font-bold text-foreground">
                  Maintenance Reminders
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className={`card-tactical ${reminderSummary.overdue > 0 ? "bg-red-500/5 border-red-500/30" : "bg-zinc-950/50 border-zinc-800"}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${reminderSummary.overdue > 0 ? "bg-red-500/10" : "bg-zinc-800"}`}>
                    <AlertTriangle className={`w-5 h-5 ${reminderSummary.overdue > 0 ? "text-red-500" : "text-zinc-500"}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Overdue</p>
                    <p className={`text-2xl font-mono font-bold ${reminderSummary.overdue > 0 ? "text-red-500" : "text-foreground"}`}>
                      {reminderSummary.overdue}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`card-tactical ${reminderSummary.dueSoon > 0 ? "bg-amber-500/5 border-amber-500/30" : "bg-zinc-950/50 border-zinc-800"}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${reminderSummary.dueSoon > 0 ? "bg-amber-500/10" : "bg-zinc-800"}`}>
                    <Clock className={`w-5 h-5 ${reminderSummary.dueSoon > 0 ? "text-amber-500" : "text-zinc-500"}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Due Soon</p>
                    <p className={`text-2xl font-mono font-bold ${reminderSummary.dueSoon > 0 ? "text-amber-500" : "text-foreground"}`}>
                      {reminderSummary.dueSoon}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Active</p>
                    <p className="text-2xl font-mono font-bold text-foreground">
                      {reminders.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notification Settings */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-muted-foreground" />
                Notification Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="dashboard-alerts">Dashboard Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Show reminders on the garage dashboard
                  </p>
                </div>
                <Switch
                  id="dashboard-alerts"
                  checked={preferences.dashboard_alerts}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, dashboard_alerts: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="email-enabled" className="flex items-center gap-2">
                    Email Notifications
                    {!preferences.email_configured && (
                      <span className="text-xs text-amber-500">(Not configured)</span>
                    )}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Receive email reminders for due services
                  </p>
                </div>
                <Switch
                  id="email-enabled"
                  checked={preferences.email_enabled}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, email_enabled: checked }))}
                  disabled={!preferences.email_configured}
                />
              </div>

              {/* Push Notifications */}
              {pushEnabled && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4" />
                      Push Notifications
                      {pushSubscribed && (
                        <span className="text-xs text-green-500">(Enabled)</span>
                      )}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Get browser/mobile push notifications
                    </p>
                  </div>
                  <Button
                    variant={pushSubscribed ? "outline" : "default"}
                    size="sm"
                    onClick={pushSubscribed ? handleUnsubscribePush : handleSubscribePush}
                    disabled={subscribingPush}
                    data-testid="push-toggle-btn"
                  >
                    {subscribingPush ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : pushSubscribed ? (
                      "Disable"
                    ) : (
                      "Enable"
                    )}
                  </Button>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2 pt-4 border-t border-zinc-800">
                <div className="space-y-2">
                  <Label htmlFor="miles-threshold">Default Miles Before Due</Label>
                  <Input
                    id="miles-threshold"
                    type="number"
                    value={preferences.default_reminder_miles}
                    onChange={(e) => setPreferences(p => ({ ...p, default_reminder_miles: parseInt(e.target.value) || 500 }))}
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    Remind when within this many miles of due
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="days-threshold">Default Days Before Due</Label>
                  <Input
                    id="days-threshold"
                    type="number"
                    value={preferences.default_reminder_days}
                    onChange={(e) => setPreferences(p => ({ ...p, default_reminder_days: parseInt(e.target.value) || 30 }))}
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    Remind when within this many days of due
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-4">
                <Button
                  onClick={handleSavePreferences}
                  disabled={saving}
                  data-testid="save-prefs-btn"
                  className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Preferences
                </Button>
                {preferences.email_configured && (
                  <Button
                    variant="outline"
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    data-testid="send-email-btn"
                    className="border-zinc-700 hover:bg-zinc-800"
                  >
                    {sendingEmail ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Send Email
                  </Button>
                )}
                {pushSubscribed && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleSendPushTest}
                      disabled={sendingPush}
                      data-testid="send-push-test-btn"
                      className="border-zinc-700 hover:bg-zinc-800"
                    >
                      {sendingPush ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Bell className="w-4 h-4 mr-2" />
                      )}
                      Test Push
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleSendPushReminders}
                      disabled={sendingPush}
                      data-testid="send-push-reminders-btn"
                      className="border-zinc-700 hover:bg-zinc-800"
                    >
                      {sendingPush ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <BellRing className="w-4 h-4 mr-2" />
                      )}
                      Send Push Reminders
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Reminders List */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BellRing className="w-5 h-5 text-muted-foreground" />
                Active Reminders
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reminders.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-muted-foreground">All services are up to date!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reminders.map((reminder) => (
                    <div
                      key={reminder.id}
                      className={`p-4 rounded-sm border ${
                        reminder.status === "Overdue"
                          ? "bg-red-500/5 border-red-500/30"
                          : "bg-amber-500/5 border-amber-500/30"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${
                            reminder.status === "Overdue" ? "bg-red-500/10" : "bg-amber-500/10"
                          }`}>
                            {reminder.status === "Overdue" ? (
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                            ) : (
                              <Clock className="w-4 h-4 text-amber-500" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded-sm ${
                                reminder.status === "Overdue"
                                  ? "bg-red-500/20 text-red-500"
                                  : "bg-amber-500/20 text-amber-500"
                              }`}>
                                {reminder.status}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {reminder.service_category}
                              </span>
                            </div>
                            <h4 className="font-medium text-foreground mt-1">
                              {reminder.service_title}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Truck className="w-3 h-3 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                {reminder.vehicle_nickname}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {reminder.next_due_mileage && (
                            <div className="flex items-center gap-1 text-sm">
                              <Gauge className="w-3 h-3 text-muted-foreground" />
                              <span className={`font-mono ${
                                reminder.miles_remaining && reminder.miles_remaining < 0
                                  ? "text-red-500"
                                  : "text-foreground"
                              }`}>
                                {reminder.miles_remaining !== null && (
                                  reminder.miles_remaining < 0
                                    ? `${Math.abs(reminder.miles_remaining).toLocaleString()} mi overdue`
                                    : `${reminder.miles_remaining.toLocaleString()} mi left`
                                )}
                              </span>
                            </div>
                          )}
                          {reminder.next_due_date && (
                            <div className="flex items-center gap-1 text-sm mt-1">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              <span className={`font-mono ${
                                reminder.days_remaining !== null && reminder.days_remaining < 0
                                  ? "text-red-500"
                                  : "text-foreground"
                              }`}>
                                {reminder.days_remaining !== null && (
                                  reminder.days_remaining < 0
                                    ? `${Math.abs(reminder.days_remaining)} days overdue`
                                    : `${reminder.days_remaining} days left`
                                )}
                              </span>
                            </div>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/vehicles/${reminder.vehicle_id}`)}
                            className="mt-2 text-xs"
                          >
                            View Vehicle
                            <ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
